import { isNonNil } from '@shady-pines/types';
import { useEffect } from 'react';
import { useGlobalContextStore } from '../global-context/global-context-store';
import { useRefreshEntity } from '../use-refresh-entity';
import { useUserStore } from '../user/user-store';
import { useEventStore } from './event-store';

export const useManageEvents = () => {
  const eventSubscriptionsFetching = useEventStore((s) => s.loading.subscriptions);
  const fetchEventSubscriptions = useEventStore((s) => s.fetchEventSubscriptions);
  const subscriptionsFetched = useEventStore((s) => isNonNil(s.eventSubscriptionsById));
  const userId = useUserStore((s) => s.me);
  const eventsEnabled = useGlobalContextStore(
    (s) =>
      s.globalFeatures?.find((g) => g.feature === 'events')?.enabled ||
      s.userFeatures?.find((u) => u.feature === 'events')?.enabled,
  );

  useEffect(() => {
    if (eventSubscriptionsFetching || !eventsEnabled || subscriptionsFetched) {
      return;
    }

    void fetchEventSubscriptions(userId);
  }, [
    eventSubscriptionsFetching,
    eventsEnabled,
    fetchEventSubscriptions,
    subscriptionsFetched,
    userId,
  ]);

  const eventsFetched = useEventStore((e) => isNonNil(e.events));
  const eventsFetching = useEventStore((e) => e.loading.events);
  const fetchEvents = useEventStore((e) => e.fetchEvents);
  const refreshEvents = useEventStore((e) => e.refreshEvents);

  useRefreshEntity('event', refreshEvents, fetchEvents);

  useEffect(() => {
    if (eventsFetched || eventsFetching || !eventsEnabled) {
      return;
    }

    void fetchEvents();
  }, [eventsEnabled, eventsFetched, eventsFetching, fetchEvents]);

  const featuredUsersFetched = useEventStore((e) => isNonNil(e.featuredUsersByEventId));
  const featuredUsersFetching = useEventStore((e) => e.loading.featuredUsers);
  const fetchEventFeaturedUsers = useEventStore((e) => e.fetchEventFeaturedUsers);

  useEffect(() => {
    if (featuredUsersFetched || featuredUsersFetching || !eventsEnabled) {
      return;
    }

    void fetchEventFeaturedUsers();
  }, [eventsEnabled, featuredUsersFetched, featuredUsersFetching, fetchEventFeaturedUsers]);

  const eventCategoriesFetched = useEventStore((e) => isNonNil(e.categoriesByEventId));
  const eventCategoriesFetching = useEventStore((e) => e.loading.eventCategories);
  const fetchEventCategories = useEventStore((e) => e.fetchEventCategories);

  useEffect(() => {
    if (eventCategoriesFetched || eventCategoriesFetching || !eventsEnabled) {
      return;
    }

    void fetchEventCategories();
  }, [eventsEnabled, eventCategoriesFetched, eventCategoriesFetching, fetchEventCategories]);

  const allCategoriesFetched = useEventStore((e) => isNonNil(e.allCategories));
  const allCategoriesFetching = useEventStore((e) => e.loading.allCategories);
  const fetchAllCategories = useEventStore((e) => e.fetchAllCategories);

  useEffect(() => {
    if (allCategoriesFetched || allCategoriesFetching || !eventsEnabled) {
      return;
    }

    void fetchAllCategories();
  }, [eventsEnabled, allCategoriesFetched, allCategoriesFetching, fetchAllCategories]);

  const venuesFetched = useEventStore((e) => isNonNil(e.eventVenues));
  const venuesFetching = useEventStore((e) => e.loading.venues);
  const fetchEventVenues = useEventStore((e) => e.fetchEventVenues);
  const refreshEventVenues = useEventStore((s) => s.refreshEventVenues);
  useRefreshEntity('eventVenue', refreshEventVenues, fetchEventVenues);

  useEffect(() => {
    if (venuesFetched || venuesFetching || !eventsEnabled) {
      return;
    }

    void fetchEventVenues();
  }, [eventsEnabled, venuesFetched, venuesFetching, fetchEventVenues]);
};
